import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { ArrowRight, ArrowLeft, CheckCircle } from "lucide-react";

interface JobWizardStep3Props {
  onNext: (selectedRequirements: string[]) => void;
  onBack: () => void;
  requirements: string[];
}

export function JobWizardStep3({ onNext, onBack, requirements }: JobWizardStep3Props) {
  const [selectedRequirements, setSelectedRequirements] = useState<string[]>(
    [...requirements] // All pre-selected by default
  );

  const handleToggleRequirement = (requirement: string) => {
    setSelectedRequirements(prev => 
      prev.includes(requirement)
        ? prev.filter(r => r !== requirement)
        : [...prev, requirement]
    );
  };

  const handleNext = () => {
    onNext(selectedRequirements);
  };

  return (
    <div className="min-h-screen bg-blue-100 p-6">
      <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-lg">
        <div className="bg-gradient-to-r from-accent-light/30 to-primary-light/20 border-b border-border shadow-sm rounded-t-lg backdrop-blur-sm">
          <div className="p-6 border-b border-border-light">
            <div className="flex items-center justify-between">
              <div>
                <Badge variant="outline" className="mb-2">Step 3 of 5</Badge>
                <h1 className="flex items-center gap-2 text-xl font-semibold text-foreground">
                  <CheckCircle className="w-5 h-5 text-accent" />
                  Select Requirements & Qualifications
                </h1>
                <p className="text-muted-foreground">
                  Choose the requirements and qualifications that match your background
                </p>
              </div>
              <Badge variant="secondary" className="text-lg px-3 py-1">
                {selectedRequirements.length} / {requirements.length} selected
              </Badge>
            </div>
            <div className="mt-4">
              <Progress value={60} className="w-full" />
            </div>
          </div>
        </div>

        <div className="p-6">
          <div className="space-y-4 mb-6">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">Job Requirements & Qualifications</h3>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setSelectedRequirements([...requirements])}
                >
                  Select All
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setSelectedRequirements([])}
                >
                  Deselect All
                </Button>
              </div>
            </div>
            <p className="text-muted-foreground">
              All requirements are pre-selected. Uncheck any that don't match your qualifications.
            </p>
          </div>

          <div className="grid gap-3">
            {requirements.map((requirement, index) => (
              <div 
                key={index}
                className={`flex items-start gap-3 p-4 rounded-lg border transition-all cursor-pointer hover:border-accent ${
                  selectedRequirements.includes(requirement) 
                    ? 'border-accent bg-accent-light' 
                    : 'border-border-light'
                }`}
                onClick={() => handleToggleRequirement(requirement)}
              >
                <Checkbox 
                  checked={selectedRequirements.includes(requirement)}
                  onChange={() => handleToggleRequirement(requirement)}
                  className="mt-1"
                />
                <div className="flex-1 space-y-1">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs">
                      #{index + 1}
                    </Badge>
                  </div>
                  <p className="text-sm leading-relaxed">
                    {requirement}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <div className="flex justify-between pt-6 border-t border-border-light mt-8">
            <Button variant="outline" onClick={onBack}>
              <ArrowLeft className="w-4 h-4" />
              Back
            </Button>
            <Button 
              variant="gradient" 
              onClick={handleNext}
              disabled={selectedRequirements.length === 0}
            >
              Continue to Skills
              <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}